// API client v0.4.0 — typed fetch wrappers for local-runtime static snapshots.
// state.json and providers.json are served from the Cloudflare Tunnel endpoint.
// Dynamic endpoints (BGP, latency, RSS) are legacy Workers routes; hooks handle
// their errors gracefully when the Workers backend is unavailable.
const BASE = import.meta.env.VITE_API_BASE ?? 'https://api.secdefcon.com';
const STATIC_BASE = 'https://data.secdefcon.com';

export interface ProviderItem {
  id: string;
  slug: string;
  name: string;
  tier: number;
  source_url: string;
  // Phase 2C — when "external" we don't probe directly; status is whatever
  // the upstream page advertises. UI swaps the colored dot for an "↗ external"
  // marker so visitors know we're deferring to the linked source.
  source_type?: string;
  // Phase 2D — categorization + descriptions. `category` is a slug like
  // `streaming` or `tier-0`. `description` is a 1-2 sentence blurb shown in
  // the per-provider tooltip/modal. `services_covered` is a multi-paragraph
  // longer-form description rendered in the Tier 0 modal only.
  category?: string;
  description?: string;
  services_covered?: string;
  // Override for the click-through URL. When null/undefined, the frontend
  // origin-strips source_url; when set, this is used verbatim. Required for
  // providers like Google Workspace whose JSON lives at a sub-path
  // (.../appsstatus/dashboard/incidents.json) and whose human dashboard sits
  // at a related path (...appsstatus/dashboard/), not the host root.
  dashboard_url?: string | null;
  // Operator-set letter grade. Values: 'A+' .. 'D-' or 'F'. Null = unrated.
  // 'F' triggers Tier-F reclassification — the provider is held out of the
  // category grids and surfaced only in the dedicated Tier F card.
  reputation_grade?: string | null;
  reputation_summary?: string | null;
  reputation_set_at?: string | null;
  reputation_locked_until?: string | null;
  suspended_reason?: string | null;
  suspended_at?: string | null;
  suspended_by?: string | null;
}

// Phase 2A — single active incident entry returned by the coordinator. The
// duration_ms is server-computed (now - started_at) so the client just renders.
export interface IncidentItem {
  slug: string;
  provider_name: string;
  severity: number;
  started_at: string;
  duration_ms: number;
  message?: string;
  // Test-incident drill (set via /admin/test-incident).
  is_test?: boolean;
  // Operator-set manual override (POST /admin/incidents) — used when the
  // provider's own status page misrepresents reality. `notice` is the
  // operator's free-form explanation rendered in distinct banner chrome.
  is_manual?: boolean;
  notice?: string;
}

export interface IncidentBlock {
  active: boolean;
  // True only when every active incident was admin-injected as a test.
  // A coincident real incident flips this back to false so the alert chrome
  // stays correct.
  is_test?: boolean;
  incidents: IncidentItem[];
}

export interface MaintenanceBlock {
  enabled: boolean;
  message?: string;
  started_at?: string;
  started_by?: string;
}

export interface StateResponse {
  // `since` is the ISO timestamp of when the issue was first detected (i.e.
  // started_at of the still-open status_events row). Omitted on severity=0.
  // `updated_at` tracks the last mutation to the open status_event row and is
  // used for the long-term issue shelf cutoff.
  providers: Record<
    string,
    {
      severity: number;
      message?: string;
      since?: string;
      updated_at?: string;
    }
  >;
  ticker: {
    id: string;
    html_rendered: string;
    severity: 'info' | 'warn' | 'critical';
    expires_at: string | null;
    created_at: string;
  } | null;
  incident: IncidentBlock | null;
  // System maintenance gate. When enabled the UI swaps to a maintenance
  // page; backend keeps polling so admins still see live state.
  maintenance?: MaintenanceBlock;
  fetched_at: string;
}

export interface ProviderListResponse {
  tier_0: ProviderItem[];
  // T1 + T2 share the same per-category card layout — T1 entries sort first
  // within their category (server-side ORDER BY tier, name) so visitors read
  // top-down by importance. Each item carries a `tier` field for the inline
  // T1/T2 badge.
  categories: Record<string, ProviderItem[]>;
  // Tier F (Untrustworthy) — providers reclassified out of the main grid
  // because they misrepresent reality (status page taken offline mid-outage,
  // stuck on "all green" while broken, etc.). Rendered in a dedicated card
  // at the bottom of the page.
  tier_f?: ProviderItem[];
  // Disabled providers whose public status endpoint could not be repaired.
  // They are held out of live monitoring but shown in a small explanatory card.
  suspended?: ProviderItem[];
}

export interface TierFNote {
  id: string;
  body: string;
  link_url?: string | null;
  link_label?: string | null;
  created_at: string;
}

export interface TierFDetail {
  slug: string;
  name: string;
  grade: string;
  summary: string | null;
  set_at: string | null;
  locked_until: string | null;
  notes: TierFNote[];
}

// Phase 2B — BGP event from RIS Live websocket, filtered to tier-1 ASN scope
// in bgp-worker. Optional fields reflect that announces/withdrawals carry
// different metadata; renderers must guard accordingly.
export interface BgpEvent {
  ts: number;
  type: 'UPDATE' | 'WITHDRAW' | 'ANNOUNCE';
  prefix?: string;
  asn?: number;
  path?: number[];
}

export interface BgpRecentResponse {
  events: BgpEvent[];
  summary: unknown | null;
  heartbeat: number | null;
}

// Phase 2B — Aggregated row from Analytics Engine (15-min window, p50/p95).
export interface LatencyRow {
  region: string;
  target: string;
  p50_ms: number;
  p95_ms: number;
  samples: number;
}

export type LatencyAggRow = LatencyRow;

export interface LatencyHistoryRow extends LatencyRow {
  ts: string;
}

export interface LatencyHistoryHealth {
  requestedHours: number;
  requestedRegion: string | null;
  rowCount: number;
  regionCount: number;
  targetCount: number;
  snapshotCount: number;
  latestTs: string | null;
  latestSnapshotRows: number;
  latestSnapshotTargets: number;
  latestSnapshotMultiSampleRows: number;
  latestSnapshotParityRows: number;
  latestSnapshotSpreadRows: number;
  latestSnapshotSampleMin: number | null;
  latestSnapshotSampleMax: number | null;
  latestSnapshotSampleAvg: number | null;
  latestSnapshotAgeMinutes: number | null;
  isStale: boolean;
}

// Phase 2C — aggregated RSS feed item from rss-aggregator. Worker A handles
// fetching/normalisation; this is just the wire shape the carousel consumes.
export interface RssItem {
  provider_slug: string;
  provider_name: string;
  title: string;
  link: string;
  published_at: string;
}

export async function fetchRssItems(opts: { tier?: number } = {}): Promise<RssItem[]> {
  const params = new URLSearchParams();
  if (opts.tier !== undefined) params.set('tier', String(opts.tier));
  const qs = params.toString();
  const r = await fetch(`${BASE}/rss/items${qs ? `?${qs}` : ''}`);
  if (!r.ok) throw new Error(`rss ${r.status}`);
  return r.json();
}

export async function fetchBgpRecent(): Promise<BgpRecentResponse> {
  const r = await fetch(`${BASE}/bgp/recent`);
  if (!r.ok) throw new Error(`bgp ${r.status}`);
  return r.json();
}

export async function fetchLatencyRecent(): Promise<LatencyRow[]> {
  const r = await fetch(`${BASE}/latency/recent`);
  if (!r.ok) throw new Error(`latency ${r.status}`);
  return r.json();
}

export async function fetchLatencyHistory(
  options: {
    region?: string;
    hours?: number;
  } = {},
): Promise<LatencyHistoryRow[]> {
  const params = new URLSearchParams();
  if (options.region) params.set('region', options.region);
  if (options.hours) params.set('hours', String(options.hours));
  const qs = params.toString();
  const r = await fetch(`${BASE}/latency/history${qs ? `?${qs}` : ''}`);
  if (!r.ok) throw new Error(`latency history ${r.status}`);
  return r.json();
}

export async function fetchLatencyHealth(
  options: {
    region?: string;
    hours?: number;
  } = {},
): Promise<LatencyHistoryHealth> {
  const params = new URLSearchParams();
  if (options.region) params.set('region', options.region);
  if (options.hours) params.set('hours', String(options.hours));
  const qs = params.toString();
  const r = await fetch(`${BASE}/latency/health${qs ? `?${qs}` : ''}`);
  if (!r.ok) throw new Error(`latency health ${r.status}`);
  return r.json();
}

export async function fetchState(): Promise<StateResponse> {
  const r = await fetch(`${STATIC_BASE}/state.json`);
  if (!r.ok) throw new Error(`state ${r.status}`);
  return r.json();
}

export async function fetchProviders(): Promise<ProviderListResponse> {
  const r = await fetch(`${STATIC_BASE}/providers.json`);
  if (!r.ok) throw new Error(`providers ${r.status}`);
  return r.json();
}

export async function fetchTierFDetail(slug: string): Promise<TierFDetail> {
  const r = await fetch(`${BASE}/tier-f/${encodeURIComponent(slug)}/notes`);
  if (!r.ok) throw new Error(`tier-f ${r.status}`);
  return r.json();
}
